/**
 * Grupo de zoom da toolbar da mesa (E37.F1.T02) + toggle de sincronização // ux-neutrality:allow doc-comment
 * K↔Q (T03). E37.F2: ícones lucide + tooltip Radix (rótulo + atalho). // ux-neutrality:allow doc-comment
 *
 * Comandos são despachados pelo mesmo ZoomCommand consumido pelos dois modos // ux-neutrality:allow doc-comment
 * (Comparar e Sobrepor & alinhar) — apresentação-only, nada vira medida. // ux-neutrality:allow doc-comment
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link2, Minus, Plus, ZoomIn } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { ptBR } from '@/i18n/pt-BR';
import type { ZoomCommand } from './ZoomPanViewport';

export const ZOOM_PRESET_PERCENTS = [50, 100, 200, 400] as const;

export interface ZoomToolbarControlsProps {
  onCommand: (action: ZoomCommand['action'], percent?: number) => void;
  /** Sync só tem sentido no Comparar (dois painéis); fora dele o toggle fica
   * desabilitado com tooltip explicativo (mesmo padrão da régua). */
  syncAvailable: boolean;
  syncPanels: boolean;
  onToggleSync: () => void;
}

export function ZoomToolbarControls({
  onCommand,
  syncAvailable,
  syncPanels,
  onToggleSync,
}: ZoomToolbarControlsProps): JSX.Element {
  return (
    <div className="toolbar-group" role="group" aria-label={ptBR.workspaceCaso.zoomMenu}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="toolbar-btn"
            data-testid="zoom-out"
            aria-label={ptBR.workspaceCaso.zoomOut}
            onClick={() => onCommand('out')}
          >
            <Minus className="toolbar-icon" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{ptBR.workspaceCaso.zoomOut}</TooltipContent>
      </Tooltip>

      <DropdownMenu.Root>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="toolbar-btn"
                data-testid="zoom-menu"
                aria-label={ptBR.workspaceCaso.zoomMenu}
              >
                <ZoomIn className="toolbar-icon" />
              </button>
            </DropdownMenu.Trigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{ptBR.workspaceCaso.zoomMenu}</p>
            <p className="toolbar-tooltip-hint">{ptBR.workspaceCaso.zoomGestureTitle}</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="zoom-menu-content" align="start" sideOffset={4}>
            <DropdownMenu.Item
              className="zoom-menu-item"
              data-testid="zoom-fit"
              onSelect={() => onCommand('fit')}
            >
              {ptBR.workspaceCaso.zoomFit}
            </DropdownMenu.Item>
            {ZOOM_PRESET_PERCENTS.map((percent) => (
              <DropdownMenu.Item
                key={percent}
                className="zoom-menu-item"
                data-testid={`zoom-${percent}`}
                onSelect={() =>
                  percent === 100 ? onCommand('hundred') : onCommand('percent', percent)
                }
              >
                {`${percent}%`}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="toolbar-btn"
            data-testid="zoom-in"
            aria-label={ptBR.workspaceCaso.zoomIn}
            onClick={() => onCommand('in')}
          >
            <Plus className="toolbar-icon" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{ptBR.workspaceCaso.zoomIn}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="toolbar-btn"
            data-testid="sync-toggle"
            aria-pressed={syncAvailable && syncPanels}
            disabled={!syncAvailable}
            aria-label={ptBR.workspaceCaso.syncPanels}
            onClick={onToggleSync}
          >
            <Link2 className="toolbar-icon" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{ptBR.workspaceCaso.syncPanels}</p>
          <p className="toolbar-tooltip-hint">
            {syncAvailable
              ? ptBR.workspaceCaso.syncPanelsTitle
              : ptBR.workspaceCaso.syncPanelsCompareOnly}
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
